import React from 'react';
import { usePWA } from '../../hooks/usePWA';
import {
  Snackbar,
  Alert,
  Slide,
  SlideProps,
  Typography,
  Box
} from '@mui/material';
import { 
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon 
} from '@mui/icons-material';

interface PWAOfflineIndicatorProps {
  showOnlineNotification?: boolean;
  onlineNotificationDuration?: number;
  offlineMessage?: string;
  onlineMessage?: string;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const PWAOfflineIndicator: React.FC<PWAOfflineIndicatorProps> = ({
  showOnlineNotification = true,
  onlineNotificationDuration = 3000,
  offlineMessage = "You're offline. Some features may not be available.",
  onlineMessage = "You're back online!"
}) => {
  const { isOnline } = usePWA();
  const [wasOffline, setWasOffline] = React.useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = React.useState(false);

  React.useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline && isOnline && showOnlineNotification) {
      setShowOnlineAlert(true);
      setWasOffline(false);
    }
  }, [isOnline, wasOffline, showOnlineNotification]);

  const handleOnlineAlertClose = () => {
    setShowOnlineAlert(false);
  };

  return (
    <>
      {/* Offline indicator */}
      <Snackbar
        open={!isOnline}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiAlert-root': {
            minWidth: 300,
            borderRadius: 2
          }
        }}
      >
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          sx={{
            backgroundColor: 'warning.main',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              No Internet Connection
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {offlineMessage}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Online notification */}
      {showOnlineNotification && (
        <Snackbar
          open={showOnlineAlert}
          autoHideDuration={onlineNotificationDuration}
          onClose={handleOnlineAlertClose}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiAlert-root': {
              minWidth: 250,
              borderRadius: 2
            }
          }}
        >
          <Alert
            severity="success"
            icon={<OnlineIcon />}
            onClose={handleOnlineAlertClose}
            sx={{
              backgroundColor: 'success.main',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              },
              '& .MuiIconButton-root': {
                color: 'white'
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {onlineMessage}
            </Typography>
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default PWAOfflineIndicator;
